import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog19 = () => {
  const hashtags = [
    "#womenshealth",
    "#wellness",
    "#selfcare",
    "#holistichealth",
    "#hormonebalance",
    "#emotionalwellness",
    "#mindfulness",
    "#consciousliving",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>9 Health Priorities Every Woman Over 25 Can’t Afford to Miss</title>
        <meta
          name="description"
          content="Discover 9 essential health priorities every woman over 25 must focus on — from hormonal balance to sleep, hydration, and emotional wellness."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left text-3xl font-semibold mb-8">
          9 Health Priorities Every Woman Over 25 Can’t Afford to Miss
        </h1>

        <p className="text-center italic text-gray-600 mb-6">
          Because your body isn’t asking for perfection — it’s asking for
          attention.
        </p>
           <img
          src="/assets/knowyourself/blogs/blog19.png"
          alt="women's body"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10 ml-10"
        />


        {/* Intro */}
        <section className="space-y-4 mb-8">
          <p>Let’s start with something honest.</p>

          <p>
            If you’re a woman in your mid or late 20s, chances are…
            <br />
            you’re juggling a hundred roles — work deadlines, relationships,
            home responsibilities, social expectations — while silently
            ignoring your own health.
          </p>

          <p>
            You tell yourself,
            <br />
            “I’m fine.”
            <br />
            “It’s just stress.”
            <br />
            “I’ll check that later.”
          </p>

          <p>
            But “later” never comes.
            <br />
            And that’s how most women slip into burnout, hormonal imbalance,
            fatigue, and emotional chaos without even realizing it.
          </p>

          <p className="text-[#578d48] font-semibold">
            Your body doesn’t need you to be stronger —
            <br />
            it needs you to be more attentive.
          </p>

          <p className="text-[#578d48] font-semibold">
            Here are 9 essential health priorities every woman should hold
            close — starting today.
          </p>
        </section>

        {/* Points */}
        <section className="space-y-6 mb-10">
          <div>
            <h3 className="text-primary font-semibold">
              1. Don’t Ignore Period Pain
            </h3>

            <p>
              Period pain isn’t “normal.”
              <br />
              It’s common, but that doesn’t mean it’s healthy.
            </p>

            <p>
              If your cycle leaves you in bed, popping painkillers every month,
              it’s your body whispering that something’s off — maybe your
              hormones, your stress, or your gut health.
            </p>

            <p className="text-[#578d48]">
              👉 Listen to those whispers before they become screams.
            </p>

            <p className="text-[#578d48]">
              Try This: Track your cycles. Note changes in pain, flow, or mood.
              Consult a gynecologist or Ayurvedic practitioner early — not after
              it becomes unbearable.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              2. Check Your Vitamin D & Iron Regularly
            </h3>

            <p>
              Fatigue, hair fall, mood swings, low motivation — these aren’t
              just “mental.”
              <br />
              Often, they’re biochemical signals of deficiencies.
            </p>

            <p>
              Most Indian women, especially working indoors, have low Vitamin D
              and iron levels.
              <br />
              And that one imbalance can affect everything — from focus to
              fertility.
            </p>

            <p className="text-[#578d48]">
              Try This:
              <br />
              Get a simple blood test every 6 months.
              <br />
              Include sun exposure, green leafy veggies, dates, jaggery, and
              soaked nuts in your diet.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              3. Drink Enough Water Daily
            </h3>

            <p>
              Sounds basic. Yet it’s the first thing we forget.
              <br />
              Dehydration affects digestion, hormones, and even your emotional
              stability.
            </p>

            <p className="text-[#578d48]">
              Water doesn’t just hydrate your skin — it detoxes your mind.
            </p>

            <p className="text-[#578d48]">
              Try This:
              <br />
              Keep a copper or glass bottle by your side.
              <br />
              Sip throughout the day — not just when you feel thirsty.
              <br />
              Start your morning with 1 glass of warm water before tea or
              coffee.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. Say No to Back-to-Back Commitments
            </h3>

            <p>
              Every “yes” you give to the world is a “no” you take from
              yourself.
              <br />
              You’re not weak for needing rest — you’re human.
            </p>

            <p>
              Overcommitting creates a nervous system that’s always in
              fight-or-flight mode.
              <br />
              That’s where anxiety, insomnia, and burnout start.
            </p>

            <p className="text-[#578d48]">
              Try This:
              <br />
              Pause before you say yes. Ask — “Can I really hold space for
              this?”
              <br />
              Your peace matters more than being “available.”
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              5. Keep Up With Routine Health Checkups
            </h3>

            <p>
              Prevention isn’t a luxury — it’s wisdom.
              <br />
              Most women wait for symptoms, but real self-care is catching
              issues before they grow.
            </p>

            <p>
              Get your annual full-body check-up — thyroid, sugar, lipid
              profile, iron, Vitamin D, and reproductive health markers.
            </p>

            <p className="text-[#578d48]">
              Try This:
              <br />
              Make it a yearly ritual. Block the date in your calendar like a
              festival.
              <br />
              Health awareness is self-respect in action.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              6. Move Your Body for Strength, Not Just Shape
            </h3>

            <p>
              Forget what Instagram says.
              <br />
              Fitness isn’t about abs or thigh gaps — it’s about energy,
              stamina, and emotional stability.
            </p>

            <p className="text-[#578d48]">
              Your body doesn’t want perfection.
              <br />
              It wants movement — the kind that builds strength and clears your
              mind.
            </p>

            <p className="text-[#578d48]">
              Try This:
              <br />
              Pick what feels joyful — yoga, walking, dancing, resistance
              training.
              <br />
              Even 20 minutes a day changes your hormones and mood.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              7. Choose Sleep Over Late-Night Scrolling
            </h3>

            <p>
              That one extra reel is costing you your calm.
              <br />
              Sleep isn’t a reward. It’s the foundation of every healing
              process — mental, emotional, physical.
            </p>

            <p className="text-[#578d48]">
              When you sacrifice sleep, you’re trading peace for dopamine hits.
            </p>

            <p className="text-[#578d48]">
              Try This:
              <br />
              Turn off screens 30 minutes before bed.
              <br />
              Dim the lights, stretch, and do 5 deep breaths.
              <br />
              Sleep before 11 p.m. — your body repairs best between 10 p.m. –
              2 a.m.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              8. Take Breaks Without Guilt
            </h3>

            <p>
              Resting doesn’t make you lazy.
              <br />
              The world will keep moving, even if you pause.
              <br />
              And you’ll return with twice the focus when you do.
            </p>

            <p>
              The guilt you feel for resting is learned — not natural.
            </p>

            <p className="text-[#578d48]">
              Try This:
              <br />
              Schedule pauses like meetings.
              <br />
              Take a slow tea break. Walk barefoot on grass.
              <br />
              Your nervous system heals in stillness, not speed.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              9. Make Time for Something That Brings You Joy
            </h3>

            <p>
              Not productivity. Not obligation.
              <br />
              Pure, genuine joy.
            </p>

            <p>
              Paint. Sing. Cook. Journal. Garden. Sit quietly in sunlight.
            </p>

            <p className="text-[#578d48]">
              Joyful moments release endorphins that heal your mind faster than
              any medicine.
            </p>

            <p className="text-[#578d48]">
              Try This:
              <br />
              Give yourself 15 minutes daily for you.
              <br />
              You don’t have to “earn” your joy — it’s your birthright.
            </p>
          </div>
        </section>

        {/* Final Thought */}
        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            Final Thought 🌿
          </h2>

          <p>
            Every woman over 25 stands at a delicate crossroads — between youth
            and maturity, energy and exhaustion, giving and grounding.
          </p>

          <p className="text-[#578d48]">
            This phase of life doesn’t need more hustle.
            <br />
            It needs awareness. Compassion. Rhythm.
          </p>

          <p className="text-[#578d48]">
            Your body is not a machine to push harder —
            <br />
            It’s a temple that asks for care, consistency, and conscious
            attention.
          </p>

          <p className="text-[#578d48] font-semibold">
            Wisdom Lens:
            <br />
            “Take care of your body like it belongs to someone you love.”
            <br />— Param Life Principle
          </p>

          <p className="text-[#578d48] font-semibold">
            Journal Prompt:
            <br />
            “What is one small act of self-care I’ve been avoiding — and how can
            I begin it today without guilt?”
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog19;
